/**
 * `<DecisionsRoute>` — `/w/:slug/decisions`
 *
 * The decisions inbox: pending asks the AI raised via `request_decision`. A
 * support agent approves/denies each one; the AI announces the ruling on the
 * conversation's next turn. List needs `conversation:monitor`; ruling needs
 * `decision:rule`. Polls so the queue stays fresh.
 */

import { useAbility, useWorkspace } from "@cometchat-ai/auth/client";
import { Button, Input, TableBody, TableHeader, toast } from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Scale } from "../components/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
  DataGrid,
  DataGridCell,
  DataGridHead,
  DataGridHeaderRow,
  DataGridRow,
} from "../components/data-grid";
import { PageShell } from "../components/page-shell";
import { EmptyState } from "../components/empty-state";
import { ErrorState } from "../components/error-state";
import { ListSkeleton } from "../components/list-skeleton";
import { formatApiError } from "../lib/format-error";
import { type InboxDecision, decisionKeys, listDecisions, ruleDecision } from "../handoff/api";

function who(d: InboxDecision): string {
  return d.contact?.displayName ?? d.contact?.email ?? d.sessionId.slice(0, 8);
}

export function DecisionsRoute() {
  const { t } = useTranslation("workspace");
  const { workspace } = useWorkspace();
  const ability = useAbility();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canRule = ability.can("rule", "Decision");
  const [notes, setNotes] = useState<Record<string, string>>({});

  const query = useQuery({
    queryKey: decisionKeys.list(workspace.slug),
    queryFn: () => listDecisions(),
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
  });

  const rule = useMutation({
    mutationFn: (vars: { id: string; outcome: "accepted" | "denied"; note?: string }) =>
      ruleDecision(vars.id, vars.outcome, vars.note?.trim() || undefined),
    onSuccess: (_data, vars) => {
      toast.success(
        vars.outcome === "accepted" ? t("decisions.approvedToast") : t("decisions.deniedToast"),
      );
      void queryClient.invalidateQueries({ queryKey: decisionKeys.list(workspace.slug) });
    },
    onError: (e) => toast.error(t("decisions.failed"), { description: formatApiError(e) }),
  });

  const items = query.data?.items ?? [];

  return (
    <PageShell className="p-0" data-testid="decisions-page" description={t("decisions.subtitle")}>
      <div className="flex flex-col py-2">
        {query.isPending ? (
          <ListSkeleton rows={3} itemClassName="h-10 w-full" className="space-y-2 px-6" />
        ) : query.error ? (
          <div className="px-6">
            <ErrorState message={query.error.message} onRetry={query.refetch} />
          </div>
        ) : items.length === 0 ? (
          <div className="px-6 py-12">
            <EmptyState data-testid="decisions-empty" icon={Scale} title={t("decisions.empty")} />
          </div>
        ) : (
          <DataGrid className="border-t border-border">
            <TableHeader>
              <DataGridHeaderRow>
                <DataGridHead className="pl-6">{t("decisions.table.customer")}</DataGridHead>
                <DataGridHead>{t("decisions.table.ask")}</DataGridHead>
                <DataGridHead className="w-[160px]">{t("decisions.table.subject")}</DataGridHead>
                <DataGridHead className="w-[360px] pr-6 text-right" divider={false}>
                  {t("decisions.table.actions")}
                </DataGridHead>
              </DataGridHeaderRow>
            </TableHeader>
            <TableBody>
              {items.map((d) => {
                const busy = rule.isPending && rule.variables?.id === d.id;
                return (
                  <DataGridRow key={d.id} data-testid={`decision-row-${d.id}`}>
                    <DataGridCell className="truncate pl-6 text-[13px] text-foreground">
                      {who(d)}
                    </DataGridCell>
                    <DataGridCell className="text-[13px] text-foreground">
                      {d.questionText}
                    </DataGridCell>
                    <DataGridCell className="text-[13px] text-muted-foreground">
                      {d.subjectKey ?? "—"}
                    </DataGridCell>
                    <DataGridCell className="pr-6 text-right" divider={false}>
                      <div className="inline-flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            navigate(`/w/${workspace.slug}/conversations?c=${d.sessionId}`)
                          }
                          className="h-7 rounded-md text-[13px]"
                          data-testid={`decision-view-${d.id}`}
                        >
                          {t("decisions.viewConversation")}
                        </Button>
                        {canRule ? (
                          <>
                            <Input
                              value={notes[d.id] ?? ""}
                              onChange={(e) => setNotes((n) => ({ ...n, [d.id]: e.target.value }))}
                              placeholder={t("decisions.notePlaceholder")}
                              className="h-7 w-40 rounded-md text-[13px] md:text-[13px]"
                              data-testid={`decision-note-${d.id}`}
                            />
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={busy}
                              onClick={() =>
                                rule.mutate({ id: d.id, outcome: "denied", note: notes[d.id] })
                              }
                              className="h-7 rounded-md text-[13px]"
                              data-testid={`decision-deny-${d.id}`}
                            >
                              {t("decisions.deny")}
                            </Button>
                            <Button
                              size="sm"
                              disabled={busy}
                              onClick={() =>
                                rule.mutate({ id: d.id, outcome: "accepted", note: notes[d.id] })
                              }
                              className="h-7 gap-1.5 rounded-md text-[13px] hover:bg-primary-hover"
                              data-testid={`decision-approve-${d.id}`}
                            >
                              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                              {t("decisions.approve")}
                            </Button>
                          </>
                        ) : null}
                      </div>
                    </DataGridCell>
                  </DataGridRow>
                );
              })}
            </TableBody>
          </DataGrid>
        )}
      </div>
    </PageShell>
  );
}
